/**
 * Stock & articles dashboard widgets.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useArticles, useArticleCategories } from '@/hooks/use-articles';
import { useAllStock, useWarehouses } from '@/hooks/use-stock';
import { Widget, TopList, HBarList, BigStat, fmtMoney, fmtCount } from './common';
import type { TopListRow } from './common';

// ---- Articles total (active + inactive) ----

export function ArticlesTotalWidget() {
  const { t } = useTranslation();
  const { data: articles, isLoading } = useArticles();

  const stat = useMemo(() => {
    const all = articles ?? [];
    return {
      total: all.length,
      active: all.filter((a) => a.isActive).length,
    };
  }, [articles]);

  return (
    <Widget title={t('dashboard.articlesTotal')} description={t('dashboard.articlesTotalHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtCount(stat.total)}
        sub={t('dashboard.activeCount', { count: stat.active })}
      />
    </Widget>
  );
}

// ---- Low stock alerts (articles under stock_min across all warehouses) ----

export function LowStockWidget() {
  const { t } = useTranslation();
  const { data: articles, isLoading: aLoad } = useArticles();
  const { data: stockRows, isLoading: sLoad } = useAllStock();

  const stat = useMemo(() => {
    // Sum stock per article across warehouses
    const byArticle = new Map<string, number>();
    for (const r of stockRows ?? []) {
      byArticle.set(r.articleId, (byArticle.get(r.articleId) ?? 0) + r.quantity);
    }
    let count = 0;
    const items: TopListRow[] = [];
    for (const a of articles ?? []) {
      if (!a.isActive) continue;
      if (a.stockMin <= 0) continue;
      const qty = byArticle.get(a.id) ?? 0;
      if (qty < a.stockMin) {
        count++;
        items.push({
          id: a.id,
          label: a.name,
          sublabel: `${a.code} · ${t('dashboard.stockMin')}: ${a.stockMin}`,
          value: qty,
          unit: a.unit || '',
          decimals: 0,
        });
      }
    }
    items.sort((a, b) => a.value - b.value);
    return { count, items: items.slice(0, 5) };
  }, [articles, stockRows, t]);

  return (
    <Widget
      title={t('dashboard.lowStock')}
      description={t('dashboard.lowStockHelp')}
      isLoading={aLoad || sLoad}
      action={stat.count > 0 ? <span className="text-xs text-rose-600 font-semibold">{fmtCount(stat.count)}</span> : null}
      isEmpty={stat.items.length === 0}
      emptyLabel={t('dashboard.noLowStock')}
    >
      <TopList rows={stat.items} />
    </Widget>
  );
}

// ---- Stock valuation (sum quantity × purchase price) ----

export function StockValuationWidget() {
  const { t } = useTranslation();
  const { data: articles, isLoading: aLoad } = useArticles();
  const { data: stockRows, isLoading: sLoad } = useAllStock();

  const total = useMemo(() => {
    const priceMap = new Map((articles ?? []).map((a) => [a.id, a.purchasePriceHt]));
    return (stockRows ?? []).reduce((s, r) => s + r.quantity * (priceMap.get(r.articleId) ?? 0), 0);
  }, [articles, stockRows]);

  return (
    <Widget title={t('dashboard.stockValuation')} description={t('dashboard.stockValuationHelp')} isLoading={aLoad || sLoad}>
      <BigStat
        value={fmtMoney(total, 3)}
        unit="DT"
        sub={t('dashboard.stockValuationNote')}
      />
    </Widget>
  );
}

// ---- Top categories by article count ----

export function TopCategoriesWidget() {
  const { t } = useTranslation();
  const { data: articles, isLoading: aLoad } = useArticles();
  const { data: categories, isLoading: cLoad } = useArticleCategories();

  const rows: TopListRow[] = useMemo(() => {
    const catMap = new Map((categories ?? []).map((c) => [c.id, c]));
    const byCat = new Map<string, number>();
    for (const a of articles ?? []) {
      if (!a.categoryId) continue;
      byCat.set(a.categoryId, (byCat.get(a.categoryId) ?? 0) + 1);
    }
    return Array.from(byCat.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([catId, count]) => ({
        id: catId,
        label: catMap.get(catId)?.name ?? '—',
        value: count,
      }));
  }, [articles, categories]);

  return (
    <Widget
      title={t('dashboard.topCategoriesByArticles')}
      isLoading={aLoad || cLoad}
      isEmpty={rows.length === 0}
      emptyLabel={t('dashboard.noCategories')}
    >
      <HBarList rows={rows} barClassName="bg-sky-500" />
    </Widget>
  );
}

// ---- Warehouses count ----

export function WarehousesWidget() {
  const { t } = useTranslation();
  const { data: warehouses, isLoading: wLoad } = useWarehouses();
  const { data: stockRows, isLoading: sLoad } = useAllStock();

  const stat = useMemo(() => {
    // Compute total qty per warehouse
    const byWh = new Map<string, number>();
    for (const r of stockRows ?? []) {
      byWh.set(r.warehouseId, (byWh.get(r.warehouseId) ?? 0) + r.quantity);
    }
    return {
      count: (warehouses ?? []).length,
      totalUnits: Array.from(byWh.values()).reduce((s, n) => s + n, 0),
    };
  }, [warehouses, stockRows]);

  return (
    <Widget title={t('dashboard.warehousesStat')} description={t('dashboard.warehousesHelp')} isLoading={wLoad || sLoad}>
      <BigStat
        value={fmtCount(stat.count)}
        sub={t('dashboard.totalUnitsInStock', { count: fmtCount(stat.totalUnits) })}
      />
    </Widget>
  );
}
